<template>
  <div v-if="match" class="match-drawer-backdrop" @click.self="close">
    <aside class="match-drawer">
      <header class="drawer-header">
        <h2 class="drawer-title">{{ $t('championship.matchDetails.title') }}</h2>
        <button class="drawer-close" type="button" @click="close" aria-label="close">
          <i class="fa-regular fa-times"></i>
        </button>
      </header>

      <div class="drawer-body">
          <!-- Top opponent strip: card | VS | card -->
          <div class="opponent-strip">
            <template v-for="(slot, idx) in slots">
              <div
                :key="`strip-${idx}`"
                class="opponent-strip-card"
                :class="slot.stripClass"
              >
                <img
                  v-if="slot.profilePicture"
                  :src="slot.profilePicture"
                  :alt="slot.name"
                  class="strip-avatar"
                />
                <div v-else class="strip-avatar strip-avatar-placeholder">
                  <i class="fa-regular fa-user" aria-hidden="true"></i>
                </div>
                <div class="strip-meta">
                  <div class="strip-name">{{ slot.name || $t('championship.toBeDefined') }}</div>
                  <div v-if="slot.realName" class="strip-realname">{{ slot.realName }}</div>
                  <span v-if="slot.placeBadge" class="strip-tag" :class="slot.tagClass">
                    <i class="fa-regular fa-medal" aria-hidden="true"></i>
                    {{ slot.placeBadge }}
                  </span>
                </div>
              </div>
              <div
                v-if="idx === 0 && slots.length === 2"
                :key="`vs-${idx}`"
                class="opponent-strip-vs"
              >
                {{ $t('championship.vs') }}
              </div>
            </template>
          </div>

          <!-- Two-column body: goals card + stacked stats per opponent -->
          <div class="opponents-grid">
            <template v-for="(slot, idx) in slots">
            <div :key="`divider-${idx}`" v-if="idx > 0" class="opponents-divider"></div>
            <div
              :key="`col-${idx}`"
              class="opponent-column"
            >
              <!-- Goals card: participant score rendered as goals. The 1-goal
                   = 100-lots correspondence is championship-themed copy (no
                   backend field backs the ratio yet). -->
              <div class="goals-card">
                <div class="goals-value">
                  {{ $t('championship.matchDetails.goalsAmount', { count: formatGoals(slot.score) }) }}
                </div>
                <div class="goals-label">{{ goalsEquivalenceLabel }}</div>
              </div>

              <div class="stats-stack">
                <div class="stat-card">
                  <div class="stat-label">{{ $t('championship.matchDetails.totalTrades') }}</div>
                  <div class="stat-value">
                    <span v-if="loadingAnalytics" class="stat-skeleton"></span>
                    <span v-else>{{ formatTotalTrades(slot.analytics) }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">{{ $t('championship.matchDetails.winRate') }}</div>
                  <div class="stat-value">
                    <span v-if="loadingAnalytics" class="stat-skeleton"></span>
                    <span v-else>{{ formatWinRate(slot.analytics) }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">{{ $t('championship.matchDetails.riskReward') }}</div>
                  <div class="stat-value">
                    <span v-if="loadingAnalytics" class="stat-skeleton"></span>
                    <span v-else>{{ formatRiskReward(slot.analytics) }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <div class="stat-label">{{ $t('championship.matchDetails.profitFactor') }}</div>
                  <div class="stat-value">
                    <span v-if="loadingAnalytics" class="stat-skeleton"></span>
                    <span v-else>{{ formatProfitFactor(slot.analytics) }}</span>
                  </div>
                </div>
              </div>
            </div>
            </template>
          </div>
      </div>
    </aside>
  </div>
</template>

<script>
import TournamentRepository from '@/repositories/tournament';
import { getLotsPerGoal } from '@/services/championship.service';

export default {
  name: 'ChampionshipMatchDetailsDrawer',
  props: {
    match: { type: Object, default: null },
    overview: { type: Object, default: null },
    stagesById: { type: Object, default: () => ({}) },
    // Championship participants (public payload), keyed by userId — the source
    // of opponent identity (login / skinCharacterName / skinImageUrl). We don't
    // hit the authenticated tournament-overview endpoint because the
    // championship view is public.
    participants: { type: Array, default: () => [] },
  },
  data() {
    return {
      loadingAnalytics: false,
      // Keyed by participantId — the id the match carries and the analytics
      // endpoint accepts.
      analyticsByParticipantId: {},
    };
  },
  computed: {
    participantsByUserId() {
      const map = {};
      (this.participants || []).forEach((p) => { if (p?.userId != null) map[p.userId] = p; });
      return map;
    },
    // "Each goal equals N lots" — N is hardcoded per championship for now
    // (see getLotsPerGoal); backend will provide it as a param later.
    goalsEquivalenceLabel() {
      return this.$t('championship.matchDetails.goalsEquivalence', { lots: getLotsPerGoal(this.overview) });
    },
    slots() {
      const out = [];
      // Render slot A if the match has either side populated; same for B.
      if (this.match?.userAId || this.match?.participantAId) out.push(this.buildSlot('A'));
      if (this.match?.userBId || this.match?.participantBId) out.push(this.buildSlot('B'));
      return out;
    },
    // Slot currently winning the match — A, B, or null when tied / no scores.
    // Compares the match's scoreA / scoreB.
    leadingSlot() {
      if (!this.match) return null;
      const scoreA = this.resolveLiveScore('A');
      const scoreB = this.resolveLiveScore('B');
      if (scoreA == null && scoreB == null) return null;
      if (scoreA == null) return 'B';
      if (scoreB == null) return 'A';
      if (scoreA > scoreB) return 'A';
      if (scoreB > scoreA) return 'B';
      return null;
    },
  },
  watch: {
    match: {
      immediate: true,
      handler(m) {
        this.analyticsByParticipantId = {};
        if (m) this.loadAnalytics();
      },
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    // Pull total-trades / win-rate / risk-reward / profit-factor for both
    // opponents from the public mini-analytics endpoint (internal tournamentId
    // + participantId). Fired in parallel; failures for one opponent don't
    // block the other.
    async loadAnalytics() {
      const participantIds = [this.match?.participantAId, this.match?.participantBId]
        .filter(Boolean);
      if (!participantIds.length) return;
      const stage = this.stagesById[this.match?.stageId];
      const tournamentId = stage?.tournamentId;
      const startDate = stage?.startDate || null;
      const endDate = stage?.endDate || null;
      if (!tournamentId) return;
      this.loadingAnalytics = true;
      try {
        await Promise.all(participantIds.map((participantId) => this.loadAnalyticsForParticipant(
          participantId, tournamentId, startDate, endDate,
        )));
      } finally {
        this.loadingAnalytics = false;
      }
    },
    async loadAnalyticsForParticipant(participantId, tournamentId, startDate, endDate) {
      if (!tournamentId || !participantId) return;
      try {
        const res = await TournamentRepository.getParticipantAnalyticsMini(
          tournamentId, participantId, startDate, endDate,
        );
        const row = res?.data?.content?.[0] || null;
        if (row) this.$set(this.analyticsByParticipantId, participantId, row);
      } catch (err) {
        console.error('[championship] match drawer analytics fetch failed', participantId, err);
      }
    },
    buildSlot(slot) {
      const participantId = slot === 'A' ? this.match.participantAId : this.match.participantBId;
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      // Identity comes from the championship participants payload (keyed by
      // userId). It carries login + skin fields, but no real first/last name.
      const p = userId != null ? this.participantsByUserId[userId] : null;
      const score = this.resolveLiveScore(slot);
      const fallbackName = userId
        ? this.$t('championship.userFallback', { id: userId })
        : this.$t('championship.toBeDefined');
      // "Leading" / "trailing" reflect the live score gap so the gold/gray
      // styling updates as the match progresses. When tied (or both unknown) we
      // render both sides as default and skip the medal badge.
      const leadingSlot = this.leadingSlot;
      const isLeading = leadingSlot === slot;
      const isTrailing = leadingSlot && leadingSlot !== slot;
      // Skinned championships: the skin character name/image take over as the
      // primary identity, keeping the login as a secondary line.
      const realName = p?.login || fallbackName;
      const skinName = p?.skinCharacterName || null;
      return {
        userId,
        participantId,
        name: skinName || realName,
        // Secondary line under the skin character name (only when skinned, so
        // non-skinned slots don't render a duplicate line).
        realName: skinName ? realName : null,
        profilePicture: p?.skinImageUrl || null,
        score,
        analytics: this.analyticsByParticipantId[participantId] || null,
        placeBadge: isLeading
          ? this.$t('championship.matchDetails.firstPlace')
          : (isTrailing ? this.$t('championship.matchDetails.secondPlace') : null),
        stripClass: isLeading ? 'strip-winner' : 'strip-default',
        tagClass: isLeading ? 'tag-winner' : 'tag-default',
      };
    },
    // Score is the per-match score from the bracket (scoreA / scoreB).
    resolveLiveScore(slot) {
      const matchScore = slot === 'A' ? this.match?.scoreA : this.match?.scoreB;
      return matchScore != null ? matchScore : null;
    },
    // Participant score shown as "goals". Rendered as a whole number; the
    // unit / equivalence copy lives in i18n (goalsAmount / goalsEquivalence).
    formatGoals(v) {
      if (v == null) return '0';
      return new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 0,
      }).format(v);
    },
    formatWinRate(analytics) {
      const v = analytics?.winRate;
      if (v == null) return '—';
      return `${(v * 100).toFixed(1)}%`;
    },
    formatTotalTrades(analytics) {
      const v = analytics?.totalTrades;
      if (v == null) return '—';
      return new Intl.NumberFormat(this.$i18n.locale).format(v);
    },
    // Risk/Reward comes back as a single ratio (reward per unit of risk) and is
    // rendered as "1 : X.X" to match the design.
    formatRiskReward(analytics) {
      const v = analytics?.riskRewardRatio;
      if (v == null) return '—';
      return `1 : ${Number(v).toFixed(1)}`;
    },
    // Profit Factor comes from the mini-analytics endpoint (backend commit
    // d7d97623). Renders "—" if the field is ever absent.
    formatProfitFactor(analytics) {
      const v = analytics?.profitFactor;
      if (v == null) return '—';
      return Number(v).toFixed(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.match-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 280;
  display: flex;
  justify-content: flex-end;
}

.match-drawer {
  width: 613px;
  max-width: 100%;
  height: 100%;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.2);
  font-family: 'Avenir', sans-serif;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0 20px;
  margin: 0 24px;
  // Inset rule so it doesn't run edge-to-edge of the drawer (Figma).
  border-bottom: 1px solid #ababab;
  background: #f8f8f8;

  .drawer-title {
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-size: 30px;
    line-height: 40px;
    font-weight: 700;
    margin: 0;
    color: #14161b;
  }

  .drawer-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #333;
    font-size: 18px;
    padding: 0;
  }
}

.drawer-body {
  padding: 20px 24px 32px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── Opponent strip ─────────────────────────────────────────────────────────
.opponent-strip {
  display: flex;
  align-items: center;
  gap: 32px;
}

.opponent-strip-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 15px;
  overflow: hidden;
  min-width: 0;

  &.strip-winner {
    // Gold gradient — Figma uses a soft yellow→cream wash for the winning slot.
    background: linear-gradient(180deg, #ffecbb 0%, #fffaef 100%);
  }

  &.strip-default {
    background: linear-gradient(180deg, #dedede 0%, #fbfbfb 100%);
  }
}

.strip-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;

  &.strip-avatar-placeholder i {
    color: #9a9a9a;
    font-size: 20px;
  }
}

.strip-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.strip-name {
  font-weight: 800;
  font-size: 16px;
  line-height: 1.5;
  color: #1b1b1b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.strip-realname {
  font-weight: 500;
  font-size: 12px;
  line-height: 1.3;
  color: #6a6a6a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.strip-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 18px;
  padding: 3px 8px;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.5;
  align-self: flex-start;

  i { font-size: 13px; }

  &.tag-winner {
    color: #ffc123;
  }
  &.tag-default {
    color: #ababab;
  }
}

.opponent-strip-vs {
  font-weight: 800;
  font-size: 16px;
  color: #3b3b3b;
  flex-shrink: 0;
}

// ── Opponent column body ───────────────────────────────────────────────────
.opponents-grid {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 16px;
  align-items: start;
}

.opponent-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.opponents-divider {
  // Vertical 1px rule between the two opponent columns (Figma).
  background: #ababab;
  width: 1px;
  align-self: stretch;
  justify-self: center;
}

// Goals card: bordered card showing the participant score + equivalence copy.
.goals-card {
  border: 1px solid #ebebeb;
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;
}

.goals-value {
  font-family: 'Aeonik', 'Avenir', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.5;
  color: #3b3b3b;
}

.goals-label {
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: #3b3b3b;
}

.stats-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-card {
  background: #f8f8f8;
  border: 1px solid #ebebeb;
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;

  // Figma: label is Avenir Light (not bold), value is the heavier Aeonik face.
  // Use 700 on the value so the bold/light contrast holds even when the
  // Aeonik Medium (500) face isn't loaded and would otherwise collapse to 400.
  .stat-label {
    font-weight: 300;
    font-size: 14px;
    line-height: 1.5;
    color: #14161b;
  }

  .stat-value {
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.5;
    color: #14161b;
  }
}

.stat-skeleton {
  display: inline-block;
  width: 36px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
  background-size: 200% 100%;
  animation: stat-shimmer 1.4s ease-in-out infinite;
}

@keyframes stat-shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

// ── Dark theme ─────────────────────────────────────────────────────────────
.theme--dark .match-drawer {
  background: #14161b;

  .drawer-header {
    background: #14161b;
    border-bottom-color: rgba(255, 255, 255, 0.1);
    .drawer-title { color: #f8f8f8; }
    .drawer-close { color: #ababab; }
  }

  .opponent-strip-card.strip-default {
    background: linear-gradient(180deg, #2a2a2a 0%, #1f1f1f 100%);
  }

  .strip-name { color: #f8f8f8; }
  .strip-realname { color: #ababab; }

  .opponents-divider { background: rgba(255, 255, 255, 0.1); }

  .goals-card {
    border-color: rgba(255, 255, 255, 0.1);
    .goals-value { color: #f8f8f8; }
    .goals-label { color: #ababab; }
  }

  .stat-card {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);
    .stat-label { color: #ababab; }
    .stat-value { color: #f8f8f8; }
  }
}
</style>
